import React, { useEffect } from 'react';
import {
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  useRecordContext,
} from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import { Grid, Typography } from '@mui/material';
import {
  Person as CustomerIcon,
  Group as LeadIcon,
  AccountCircle as UserIcon,
  Build as EquipmentIcon,
} from '@mui/icons-material';
import { LocationSelector } from './shared/LocationSelector';

const entityTypeChoices = [
  { id: '', name: 'None' },
  { id: 'customer', name: 'Customer' },
  { id: 'lead', name: 'Lead' },
  { id: 'user', name: 'User' },
  { id: 'equipment', name: 'Equipment' },
];

const EntityIcon = ({ entityType }: { entityType?: string }) => {
  switch (entityType) {
    case 'customer':
      return <CustomerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />;
    case 'lead':
      return <LeadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />;
    case 'user':
      return <UserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />;
    case 'equipment':
      return <EquipmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />;
    default:
      return null;
  }
};

export const TaskEntitySelector = () => {
  // Watch for changes to entityType to show/hide the entity dropdown
  const entityType = useWatch({ name: 'entityType' });
  const entityId = useWatch({ name: 'entityId' });
  const record = useRecordContext();
  const { setValue } = useFormContext();

  // Clear entityId when entityType changes to empty or null
  useEffect(() => {
    if (!entityType) {
      setValue('entityId', null);
      setValue('customerId', null);
      setValue('customerLocationId', null);
    }
  }, [entityType, setValue]);
  
  // Sync customerId when customer is selected
  useEffect(() => {
    if (entityType === 'customer' && entityId) {
      setValue('customerId', entityId);
    } else if (entityType !== 'customer') {
      setValue('customerId', null);
      setValue('customerLocationId', null);
    }
  }, [entityType, entityId, setValue]);

  const getEntityReference = () => {
    switch (entityType) {
      case 'customer':
        return 'customers';
      case 'lead':
        return 'leads';
      case 'user':
        return 'users';
      case 'equipment':
        return 'equipment';
      default:
        return null;
    }
  };

  const getEntityOptionText = () => {
    switch (entityType) {
      case 'customer':
        // Use displayName if available, otherwise fall back to full name or company
        return (record: any) => record.displayName || 
          `${record.firstName || ''} ${record.lastName || ''}`.trim() || 
          record.company || 
          'Unknown Customer';
      case 'lead':
        return 'companyName';
      case 'user':
        return 'name';
      case 'equipment':
        return 'name';
      default:
        return 'name';
    }
  };

  const getEntityLabel = () => {
    switch (entityType) {
      case 'customer':
        return 'Related Customer';
      case 'lead':
        return 'Related Lead';
      case 'user':
        return 'Related User';
      case 'equipment':
        return 'Related Equipment';
      default:
        return 'Related Entity';
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <SelectInput
          source="entityType"
          label="Entity Type"
          choices={entityTypeChoices}
          fullWidth
          allowEmpty
        />
      </Grid>
      
      {entityType && getEntityReference() && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <EntityIcon entityType={entityType} />
            {getEntityLabel()}
          </Typography>
          <ReferenceInput
            source="entityId"
            reference={getEntityReference()!}
            label={getEntityLabel()}
          >
            <AutocompleteInput
              optionText={getEntityOptionText()}
              fullWidth
              placeholder={`Select a ${entityType}...`}
            />
          </ReferenceInput>
        </Grid>
      )}
      
      {/* Show location selector when customer is selected */}
      {entityType === 'customer' && entityId && (
        <LocationSelector 
          customerId={entityId}
          value={null}
          onChange={(locationId) => setValue('locationId', locationId)}
        />
      )}
    </>
  );
};